import { Image } from './image/image';
import { Template } from './template/template';
import { TemplateContent } from './templateContent/templateContent';

const DB_NAME = 'labelyasan-recent' // Electronで触るDBとは分ける
const DB_VERSION = 202006100526;

const STORE_TEMPLATE = 'templates';
const STORE_TEMPLATE_CONTENT = 'template-contents';
const STORE_IMAGE = 'images';

const INDEX_SEQ = 'recentUsedAt';

const LIMIT = 42;

/* template */

export function getRecentTemplates(): Promise<Template[]> {
    return getRecentItems<Template>(STORE_TEMPLATE);
}

export function putRecentTemplate(template: Template): Promise<never> {
    return putRecentItem<Template>(STORE_TEMPLATE, template);
}

/* template content */

function getRecentTemplateContents(): Promise<TemplateContent[]> {
    return getRecentItems<TemplateContent>(STORE_TEMPLATE_CONTENT);
}

export function putRecentTemplateContent(content: TemplateContent): Promise<never> {
    return putRecentItem<TemplateContent>(STORE_TEMPLATE_CONTENT, content);
}

export function getRecentTemplateContentOf(templateId: number): Promise<TemplateContent | undefined> {
    return getRecentTemplateContents().then((contents) => {
        const content = contents.find(c => c.id === templateId);
        return content;
    });
}

/* image */

export function getRecentImages(): Promise<Image[]> {
    return getRecentItems<Image>(STORE_IMAGE);
}

export function putRecentImage(image: Image): Promise<never> {
    return putRecentItem<Image>(STORE_IMAGE, image);
}

/* private */

function getOrCreateStore(db: IDBDatabase, tx: IDBTransaction, name: string): IDBObjectStore {
    return db.objectStoreNames.contains(name) ?
        tx.objectStore(name) :
        db.createObjectStore(name, {keyPath: 'id'});
}

function appendIndex(st: IDBObjectStore) {
    if (st.indexNames.contains(INDEX_SEQ)) return;
    st.createIndex(INDEX_SEQ, INDEX_SEQ);
}

function openDBRecent(): IDBOpenDBRequest {
    const od = indexedDB.open(DB_NAME, DB_VERSION);
    od.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        const tx = (e.target as IDBOpenDBRequest).transaction as IDBTransaction;
        const images = getOrCreateStore(db, tx, STORE_IMAGE);
        appendIndex(images);
        const templates = getOrCreateStore(db, tx, STORE_TEMPLATE);
        appendIndex(templates);
        const templateContents = getOrCreateStore(db, tx, STORE_TEMPLATE_CONTENT);
        appendIndex(templateContents);
    }
    return od;
}

/**
 * 最近使った項目を取得する
 */
function getRecentItems<T>(name: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
        const od = openDBRecent();
        od.onsuccess = (e) => {
            const db = (e.target as IDBOpenDBRequest).result;
            const tx = db.transaction(name);
            const st = tx.objectStore(name);
            // IEではindexが動かない
            const oc = st.index(INDEX_SEQ).openCursor(undefined, 'prev');  // 新しい順
            const ls: T[] = [];
            oc.onsuccess = (e) => {
                const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    const item = cursor.value as T;
                    ls.push(item);
                    cursor.continue();
                } else {
                    resolve(ls);
                }
            }
        }
        od.onerror = reject;
    });
}

/**
 * 最近使った項目を追加する
 */
function putRecentItem<T>(name: string, value: T): Promise<never> {
    return new Promise((resolve, reject) => {
        const od = openDBRecent();
        od.onsuccess = (e) => {
            const db = (e.target as IDBOpenDBRequest).result;
            const tx = db.transaction(name, 'readwrite');
            const st = tx.objectStore(name);
            // これから追加するのでlimitを1つ少なめに数える
            deleteOldItems(st, LIMIT - 1).then(() => {
                const pt = st.put(value);
                pt.onsuccess = () => {
                    resolve();
                }
                pt.onerror = reject;
            }).catch(reject);
        }
    });
}

// 制限を満たすように古いものから削除する
function deleteOldItems(store: IDBObjectStore, limit: number) {
    return new Promise((resolve, reject) => {
        const ct = store.count();
        ct.onsuccess = () => {
            let size = ct.result;
            const oc = store.index(INDEX_SEQ).openCursor();
            oc.onsuccess = () => {
                const cursor = oc.result;
                if (cursor && size > limit) {
                    store.delete(cursor.value.id);
                    size -= 1;
                    cursor.continue();
                } else {
                    resolve();
                }
            }
            oc.onerror = reject;
        }
        ct.onerror = reject;
    })
}
